import { c as _c } from "react/compiler-runtime";
import { useRect } from '@reach/rect';
import { isNumber } from 'lodash';
import { useState, useEffect, type JSX } from 'react';
import * as React from 'react';
import { zIndices } from '../../theme';
interface Props {
  children: React.ReactNode;
  targetRef: React.RefObject<HTMLElement | null>;
  testId?: string;
  targetAnchor?: PopoverAnchor;
  yOffset?: string | number;
  xOffset?: string | number;
  anchor?: PopoverAnchor;
  width?: number;
  zIndex?: number;
  position?: 'absolute' | 'fixed';
}
export type Rect = ReturnType<typeof useRect>;
type CSSValue = string | number;
export type PopoverAnchor = 'topLeft' | 'top' | 'topRight' | 'right' | 'bottomRight' | 'bottom' | 'bottomLeft' | 'left' | 'center';
function cssValueToString(val: CSSValue): string {
  if (isNumber(val)) {
    return `${val}px`;
  }
  return val;
}
function getPopoverTransform(anchor: PopoverAnchor): string {
  if (anchor === 'top') {
    return 'translate(-50%, 0%)';
  }
  if (anchor === 'topRight') {
    return 'translate(-100%, 0%)';
  }
  if (anchor === 'right') {
    return 'translate(-100%, -50%)';
  }
  if (anchor === 'bottomRight') {
    return 'translate(-100%, -100%)';
  }
  if (anchor === 'bottom') {
    return 'translate(-50%, -100%)';
  }
  if (anchor === 'bottomLeft') {
    return 'translate(0%, -100%)';
  }
  if (anchor === 'left') {
    return 'translate(0%, -50%)';
  }
  if (anchor === 'center') {
    return 'translate(-50%, -50%)';
  }
  // Top left by default
  return 'translate(0%, 0%)';
}
function calc(value: number, offset: CSSValue): string {
  return `calc(${value}px + ${cssValueToString(offset)})`;
}
function calculatePositionStyles({
  targetRect,
  targetAnchor = 'bottomLeft',
  anchor = 'topLeft',
  xOffset = 0,
  yOffset = 0,
  width = 'auto',
  zIndex = zIndices.tether,
  position = 'fixed'
}: {
  targetRect: Rect;
  targetAnchor?: PopoverAnchor;
  xOffset?: CSSValue;
  yOffset?: CSSValue;
  anchor?: PopoverAnchor;
  width?: CSSValue;
  zIndex?: number;
  position?: 'absolute' | 'fixed';
}): React.CSSProperties {
  const transform = getPopoverTransform(anchor);
  const sharedProperties: React.CSSProperties = {
    position,
    transform,
    width: width || 'auto',
    zIndex
  };
  if (targetAnchor === 'topLeft') {
    return {
      top: calc(targetRect?.top ?? 0, yOffset),
      left: calc(targetRect?.left ?? 0, xOffset),
      ...sharedProperties
    };
  }
  if (targetAnchor === 'top') {
    return {
      top: calc(targetRect?.top ?? 0, yOffset),
      left: calc((targetRect?.left ?? 0) + (targetRect?.width ?? 0) / 2, xOffset),
      ...sharedProperties
    };
  }
  if (targetAnchor === 'topRight') {
    return {
      top: calc(targetRect?.top ?? 0, yOffset),
      left: calc(targetRect?.right ?? 0, xOffset),
      ...sharedProperties
    };
  }
  if (targetAnchor === 'right') {
    return {
      top: calc((targetRect?.top ?? 0) + (targetRect?.height ?? 0) / 2, yOffset),
      left: calc(targetRect?.right ?? 0, xOffset),
      ...sharedProperties
    };
  }
  if (targetAnchor === 'bottomRight') {
    return {
      top: calc(targetRect?.bottom ?? 0, yOffset),
      left: calc(targetRect?.right ?? 0, xOffset),
      ...sharedProperties
    };
  }
  if (targetAnchor === 'bottom') {
    return {
      top: calc(targetRect?.bottom ?? 0, yOffset),
      left: calc((targetRect?.left ?? 0) + (targetRect?.width ?? 0) / 2, xOffset),
      ...sharedProperties
    };
  }
  if (targetAnchor === 'bottomLeft') {
    return {
      top: calc(targetRect?.bottom ?? 0, yOffset),
      left: calc(targetRect?.left ?? 0, xOffset),
      ...sharedProperties
    };
  }
  if (targetAnchor === 'left') {
    return {
      top: calc((targetRect?.top ?? 0) + (targetRect?.height ?? 0) / 2, yOffset),
      left: calc(targetRect?.left ?? 0, xOffset),
      ...sharedProperties
    };
  }
  if (targetAnchor === 'center') {
    return {
      top: calc((targetRect?.top ?? 0) + (targetRect?.height ?? 0) / 2, yOffset),
      left: calc((targetRect?.left ?? 0) + (targetRect?.width ?? 0) / 2, xOffset),
      ...sharedProperties
    };
  }
  throw new Error('Invalid anchor');
}
export interface TetherOptions {
  targetAnchor?: PopoverAnchor;
  yOffset?: string | number;
  xOffset?: string | number;
  anchor?: PopoverAnchor;
  width?: number;
  zIndex?: number;
  position?: 'absolute' | 'fixed';
}

/**
 * Accepts an element ref and will return styles that you can place on any other element to position it near the target.
 * @param targetRef HTMLElement React ref
 * @param options
 */
export function useTetherStyles(targetRef, options) {
  const $ = _c(18);
  const {
    anchor,
    xOffset,
    targetAnchor,
    yOffset,
    width,
    zIndex,
    position
  } = options;
  const targetRect = useRect(targetRef);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [styles, setStyles] = useState(t0);
  let t1;
  if ($[1] !== anchor || $[2] !== position || $[3] !== targetAnchor || $[4] !== targetRect || $[5] !== width || $[6] !== xOffset || $[7] !== yOffset || $[8] !== zIndex) {
    t1 = () => {
      if (!targetRect) {
        return;
      }
      setStyles(calculatePositionStyles({
        targetRect,
        targetAnchor,
        xOffset,
        yOffset,
        anchor,
        width,
        zIndex,
        position
      }));
    };
    $[1] = anchor;
    $[2] = position;
    $[3] = targetAnchor;
    $[4] = targetRect;
    $[5] = width;
    $[6] = xOffset;
    $[7] = yOffset;
    $[8] = zIndex;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== anchor || $[11] !== position || $[12] !== targetAnchor || $[13] !== targetRect || $[14] !== width || $[15] !== xOffset || $[16] !== yOffset) {
    t2 = [targetRect, targetAnchor, xOffset, yOffset, anchor, width, position];
    $[10] = anchor;
    $[11] = position;
    $[12] = targetAnchor;
    $[13] = targetRect;
    $[14] = width;
    $[15] = xOffset;
    $[16] = yOffset;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  useEffect(t1, t2);
  return styles;
}
export function Tether(props) {
  const $ = _c(12);
  const {
    children,
    targetRef,
    testId,
    targetAnchor: t0,
    yOffset: t1,
    xOffset: t2,
    anchor: t3,
    width,
    zIndex,
    position
  } = props;
  const targetAnchor = t0 === undefined ? "bottomLeft" : t0;
  const yOffset = t1 === undefined ? 0 : t1;
  const xOffset = t2 === undefined ? 0 : t2;
  const anchor = t3 === undefined ? "topLeft" : t3;
  let t4;
  if ($[0] !== anchor || $[1] !== position || $[2] !== targetAnchor || $[3] !== width || $[4] !== xOffset || $[5] !== yOffset || $[6] !== zIndex) {
    t4 = {
      targetAnchor,
      yOffset,
      xOffset,
      anchor,
      width,
      zIndex,
      position
    };
    $[0] = anchor;
    $[1] = position;
    $[2] = targetAnchor;
    $[3] = width;
    $[4] = xOffset;
    $[5] = yOffset;
    $[6] = zIndex;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const styles = useTetherStyles(targetRef, t4);
  let t5;
  if ($[8] !== children || $[9] !== styles || $[10] !== testId) {
    t5 = <div style={styles} data-testid={testId}>{children}</div>;
    $[8] = children;
    $[9] = styles;
    $[10] = testId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}